/**
 * AdminBrandIntel — Phase 8.38
 *
 * One unified admin page for the Brand Intelligence feature:
 *   1. Engagement leaderboard — which sentiment entities are getting the
 *      most/least public ratings (admin's bird's-eye view).
 *   2. Link a sentiment entity to a corporate customer + pick which
 *      Brand Intelligence sections show on that customer's enterprise
 *      dashboard.
 *
 * Lives at /admin/brands and is linked from the admin sidebar.
 */
import { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { formatDate } from "../lib/date";

export default function AdminBrandIntel() {
  const [tab, setTab] = useState("engagement");

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto" data-testid="admin-brands-page">
      <div className="border-b-2 border-black pb-4 mb-6">
        <div className="overline">BRAND SENTIMENT</div>
        <h1 className="text-4xl font-black tracking-tighter">Brand sentiment</h1>
        <p className="text-sm text-neutral-600 mt-1">
          Which brands Pakistan is rating — and which corporate customers see the data.
        </p>
      </div>

      <div className="flex gap-2 border-b border-neutral-200 mb-6">
        {[
          ["engagement", "📊 Engagement leaderboard"],
          ["link",       "🔗 Link brand to customer"],
        ].map(([k, lab]) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            data-testid={`tab-${k}`}
            className={`px-4 py-2 text-sm font-bold border-b-2 ${tab === k ? "border-black" : "border-transparent text-neutral-500 hover:text-black"}`}
          >{lab}</button>
        ))}
      </div>

      {tab === "engagement" && <EngagementPanel />}
      {tab === "link" && <LinkPanel />}
    </div>
  );
}

function EngagementPanel() {
  const [data, setData] = useState(() => api.getCached("/admin/brand-intel/engagement"));
  useEffect(() => {
    api.get("/admin/brand-intel/engagement")
      .then((r) => setData(r.data))
      .catch(() => toast.error("Could not load engagement leaderboard"));
  }, []);

  if (!data) return <p className="text-neutral-500 text-sm">Loading…</p>;

  return (
    <div data-testid="engagement-panel">
      <div className="grid grid-cols-3 gap-3 mb-6">
        <Stat label="Total brands tracked" value={data.total_entities} testid="stat-total" />
        <Stat label="Active in last 30 days" value={data.active_30d} accent="bg-emerald-50 border-emerald-300" testid="stat-active" />
        <Stat label="Dormant (no ratings)" value={data.dormant} accent="bg-neutral-100 border-neutral-300 opacity-70" testid="stat-dormant" />
      </div>

      <div className="overflow-x-auto border-2 border-black">
        <table className="w-full text-sm" data-testid="engagement-table">
          <thead className="bg-neutral-100 text-[11px] uppercase tracking-wider">
            <tr>
              <th className="text-left px-3 py-2">Brand / entity</th>
              <th className="text-left px-3 py-2">Type</th>
              <th className="text-right px-3 py-2">Total ratings</th>
              <th className="text-right px-3 py-2">Last 30 days</th>
              <th className="text-right px-3 py-2">Avg rating</th>
              <th className="text-left px-3 py-2">Latest activity</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.length === 0 && (
              <tr><td colSpan={6} className="px-3 py-6 text-center text-neutral-500 italic">No sentiment entities yet. Add some at /admin/sentiment-entities.</td></tr>
            )}
            {data.rows.map((r) => (
              <tr key={r.entity_id} className="border-t border-neutral-200" data-testid={`row-${r.entity_id}`}>
                <td className="px-3 py-2 font-bold">{r.name}</td>
                <td className="px-3 py-2 text-neutral-600">{r.type || "—"}</td>
                <td className="px-3 py-2 text-right font-mono">{r.total_ratings}</td>
                <td className="px-3 py-2 text-right font-mono">{r.ratings_30d > 0 ? <span className="text-emerald-700">{r.ratings_30d}</span> : <span className="text-neutral-400">0</span>}</td>
                <td className="px-3 py-2 text-right font-mono">{r.avg_rating ? `${r.avg_rating} ★` : "—"}</td>
                <td className="px-3 py-2 text-xs text-neutral-500">{r.latest_rating_at ? formatDate(r.latest_rating_at) : "never"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function LinkPanel() {
  const [customers, setCustomers] = useState(() => api.getCached("/admin/customers") || []);
  const [entities, setEntities] = useState(() => api.getCached("/admin/sentiment-entities") || []);
  const [sections, setSections] = useState(() => api.getCached("/admin/brand-intel/sections")?.sections || []);
  const [customerId, setCustomerId] = useState("");
  const [entityId, setEntityId] = useState("");
  const [enabled, setEnabled] = useState(new Set());
  const [watchlistMax, setWatchlistMax] = useState(0);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/admin/customers"),
      api.get("/admin/sentiment-entities"),
      api.get("/admin/brand-intel/sections"),
    ]).then(([c, e, s]) => {
      setCustomers(c.data || []);
      setEntities(e.data || []);
      setSections(s.data || []);
    }).catch(() => toast.error("Could not load link options"));
  }, []);

  // When customer changes, prefill current link
  useEffect(() => {
    if (!customerId) { setEntityId(""); setEnabled(new Set()); setWatchlistMax(0); return; }
    const cu = customers.find((c) => c.id === customerId);
    setEntityId(cu?.linked_sentiment_entity_id || "");
    setEnabled(new Set(cu?.brand_intel_sections || []));
    setWatchlistMax(Number(cu?.brand_watchlist_max || 0));
  }, [customerId, customers]);

  const toggle = (k) => {
    const next = new Set(enabled);
    if (next.has(k)) next.delete(k); else next.add(k);
    setEnabled(next);
  };

  const save = async () => {
    if (!customerId) { toast.error("Pick a customer first"); return; }
    setBusy(true);
    try {
      await api.put(`/admin/customers/${customerId}/brand-link`, {
        entity_id: entityId || null,
        enabled_sections: Array.from(enabled),
        brand_watchlist_max: Number(watchlistMax) || 0,
      });
      toast.success("Brand link saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-5 max-w-3xl" data-testid="link-panel">
      <div>
        <label className="overline mb-2 block">Step 1 — Pick a corporate customer</label>
        <select
          value={customerId}
          onChange={(e) => setCustomerId(e.target.value)}
          className="w-full border-2 border-black p-2 text-sm"
          data-testid="customer-select"
        >
          <option value="">— select a customer —</option>
          {customers.map((c) => (
            <option key={c.id} value={c.id}>{c.company || c.name} ({c.email})</option>
          ))}
        </select>
      </div>

      <div>
        <label className="overline mb-2 block">Step 2 — Link a sentiment entity (the brand)</label>
        <select
          value={entityId}
          onChange={(e) => setEntityId(e.target.value)}
          disabled={!customerId}
          className="w-full border-2 border-black p-2 text-sm disabled:bg-neutral-100"
          data-testid="entity-select"
        >
          <option value="">— no link / unlink —</option>
          {entities.map((e) => (
            <option key={e.id} value={e.id}>{e.name} {e.type ? `(${e.type})` : ""}</option>
          ))}
        </select>
        <p className="text-xs text-neutral-500 mt-1">
          Empty list? Create entities under /admin/sentiment-entities first.
        </p>
      </div>

      <div>
        <label className="overline mb-2 block">Step 3 — Pick which sections show on the customer's dashboard</label>
        <p className="text-xs text-neutral-500 mb-3">Each checkbox is a section on the Brand Intelligence tab of the customer's Enterprise Dashboard. Empty = no Brand Intelligence tab.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {sections.map((s) => {
            const on = enabled.has(s.key);
            return (
              <label
                key={s.key}
                data-testid={`section-${s.key}`}
                className={`flex items-start gap-3 p-3 border-2 cursor-pointer text-sm ${on ? "bg-mango/10 border-black" : "border-neutral-200 hover:border-black"} ${!customerId ? "opacity-40 cursor-not-allowed" : ""}`}
              >
                <input
                  type="checkbox"
                  checked={on}
                  disabled={!customerId}
                  onChange={() => toggle(s.key)}
                  className="mt-0.5 w-4 h-4"
                />
                <div className="min-w-0">
                  <div className="font-bold">{s.label}</div>
                  <div className="font-mono text-[10px] text-neutral-500">{s.key}</div>
                </div>
              </label>
            );
          })}
        </div>
      </div>

      <div className="border-t border-neutral-200 pt-4">
        <label className="overline mb-2 block">Step 4 — Brand watchlist cap</label>
        <div className="flex items-center gap-3">
          <input
            type="number"
            min={0}
            max={50}
            value={watchlistMax}
            onChange={(e) => setWatchlistMax(e.target.value)}
            disabled={!customerId}
            data-testid="watchlist-max-input"
            className="w-24 border-2 border-black p-2 text-sm disabled:bg-neutral-100 font-mono"
          />
          <span className="text-sm text-neutral-600">brands they may add to their dashboard watchlist (0 = feature off)</span>
        </div>
        <p className="text-xs text-neutral-500 mt-2">
          Only relevant when <b>Brand watchlist</b> is enabled above. The customer picks the brands themselves; admin only sets the cap.
        </p>
      </div>

      <div className="flex justify-end gap-2">
        <Button
          onClick={save}
          disabled={busy || !customerId}
          className="bg-black text-white rounded-none"
          data-testid="save-link"
        >
          {busy ? "Saving…" : "Save link & sections"}
        </Button>
      </div>
    </div>
  );
}

function Stat({ label, value, accent = "bg-white border-neutral-300", testid }) {
  return (
    <div className={`border-2 ${accent} p-3`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-neutral-600">{label}</div>
      <div className="text-3xl font-black mt-1">{value}</div>
    </div>
  );
}
